import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { endOfDay, startOfDay } from 'date-fns';
import debounce from 'lodash.debounce';
import { useMemo, useState, useCallback, useEffect } from 'react';
import { User } from 'lucide-react';
import {
	ChipList,
	ConditionalWrapper,
	DataGrid,
	DataGridColumn,
	FilterDateRange,
	FilterSearch,
	FilterSelect,
	RequirementChip,
	Badge,
	GeneralTooltip as Tooltip,
	Spinner,
} from '@repo/components';
import { Event, PERMISSIONS } from '@repo/shared/domain';
import { EventForm } from '@repo/forms';
import { EventEmitter, getVerifyPermission, sortToString, stringToSort } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { useLocalStorage } from '@repo/components/hooks';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';
import { getEvents, getOwnEvents, createEvent } from 'src/api/personal/events';
import { getEventTypes } from 'src/api/admin/types';
import { getAllTags } from 'src/api/admin/tags';

interface SearchParams {
	sort?: string;
	page?: number;
	search?: string;
	from?: string;
	until?: string;
	ownerFilter?: string;
	archivedFilter?: string;
}

export const Route = createFileRoute('/personal_/events/')({
	validateSearch: (search: Record<string, unknown>): SearchParams => ({
		sort: (search.sort as string) || 'startTime:desc',
		page: (search.page as number) || 0,
		search: search.search as string,
		from: search.from as string,
		until: search.until as string,
		ownerFilter: search.ownerFilter as string,
		archivedFilter: search.archivedFilter as string,
	}),
	component: EventsPage,
});

function EventsPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const search = useSearch({ from: '/personal_/events/' });
	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const { updateValue } = useLocalStorage<string>('personalDashboardEventsBackUrl');
	const [isFormOpened, setIsFormOpened] = useState(false);

	useBreadcrumbLevel(0, 'Personal Dashboard', '/personal');
	useBreadcrumbLevel(1, 'Events');

	const hasEventsPermission =
		verifyPermission([PERMISSIONS.personalAllEventsView]) || verifyPermission([PERMISSIONS.personalOwnEventsView]);

	useEffect(() => {
		if (!authLoading && !hasEventsPermission) {
			navigate({ to: '/personal/overview' });
		}
	}, [authLoading, hasEventsPermission, navigate]);

	const { data: typesData } = useQuery({
		queryKey: ['personal-event-types'],
		queryFn: () => getEventTypes(),
	});

	const { config, loading: configLoading } = useConfig();
	const featureFlags = config?.featureFlags || {};
	const usesSubEvents = featureFlags?.USES_SUBCONTRACTING || false;
	const usesTags = featureFlags?.USES_TAGS || false;

	const { data: tagsData } = useQuery({
		queryKey: ['tags'],
		queryFn: () => getAllTags(),
		enabled: usesTags,
	});

	const isViewingAllEvents =
		search.ownerFilter === 'all-events' && verifyPermission([PERMISSIONS.personalAllEventsView]);

	const queryParams = useMemo(() => {
		const params: Record<string, string> = {
			sort: search.sort || 'startTime:desc',
		};
		if (search.page) params.page = search.page;
		if (search.search) params.search = search.search;
		if (search.from) params.from = search.from;
		if (search.until) params.until = search.until;
		// Pass archivedFilter to backend (defaults to 'active-events' if not specified)
		params.archivedFilter = search.archivedFilter || 'active-events';
		if (!isViewingAllEvents && authenticatedUser?.id) {
			params.userIds = authenticatedUser.id;
		}
		return params;
	}, [search, isViewingAllEvents, authenticatedUser?.id]);

	const {
		data: eventsData,
		isLoading: eventsLoading,
		refetch: refetchEvents,
	} = useQuery({
		queryKey: ['personal-events', queryParams, isViewingAllEvents],
		queryFn: () => (isViewingAllEvents ? getEvents(queryParams) : getOwnEvents(queryParams)),
	});

	const types = typesData?.data || [];
	const tags = tagsData || [];

	const updateQueryParams = useCallback(
		(params: Partial<SearchParams>) => {
			navigate({
				to: '/personal/events',
				search: (prev) => ({ ...prev, ...params }),
			});
		},
		[navigate],
	);

	const handleSort = ([sort]: { field: string; sort: string }[]) => {
		if (sort) {
			updateQueryParams({ sort: sortToString(sort) });
		}
	};

	const handleRowClick = ({ row }: { row: Event }) => {
		updateValue(typeof window !== 'undefined' ? window.location.href : '');
		navigate({ to: `/personal/events/${row.id}` });
	};

	const handleSearchChange = debounce((searchValue: string) => {
		updateQueryParams({ search: searchValue || undefined });
	}, 350);

	const handlePaginationModelChange = ({ page }: { page: number }) => {
		updateQueryParams({ page: page });
	};

	const handleDateFilterChange = (dateFilter: { from?: string; until?: string }) => {
		updateQueryParams(dateFilter);
	};

	const handleOwnerFilterChange = (ownerFilter: string) => {
		updateQueryParams({
			ownerFilter: ownerFilter === 'all-events' ? 'all-events' : undefined,
		});
	};

	const handleArchivedFilterChange = (archivedFilter: string) => {
		updateQueryParams({ archivedFilter });
	};

	const handleCloseForm = () => {
		setIsFormOpened(false);
	};

	const handleAddEvent = async (formData: Record<string, unknown>) => {
		try {
			const result = await createEvent(formData);
			EventEmitter.emit('toast', { type: 'success', message: 'Event saved' });
			setIsFormOpened(false);
			queryClient.invalidateQueries({ queryKey: ['personal-events'] });
			updateValue(typeof window !== 'undefined' ? window.location.href : '');
			navigate({ to: `/personal/events/${result.id}` });
		} catch {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to save Event. Did you enter all data correctly?' });
		}
	};

	const revalidateEvents = () => {
		refetchEvents();
		queryClient.invalidateQueries({ queryKey: ['personal-events'] });
	};

	const columns: DataGridColumn<Event>[] = [
		{ field: 'name', headerName: 'Name', flex: 1 },
		{
			field: 'project',
			headerName: 'Project',
			flex: 1,
			renderCell: (c) => (c.value ? <Badge>{String((c.value as { name?: string }).name)}</Badge> : '-'),
			sortable: false,
		},
		{
			field: 'type',
			headerName: 'Type',
			flex: 1,
			renderCell: (c) => <RequirementChip object={c.row} />,
			sortable: false,
		},
		{
			field: 'startTime',
			headerName: 'Date From',
			width: 120,
			valueFormatter: (params) => (params.value ? new Date(String(params.value)).toLocaleDateString('en-GB') : ''),
		},
		{
			field: 'finishTime',
			headerName: 'Date To',
			width: 120,
			valueFormatter: (params) => (params.value ? new Date(String(params.value)).toLocaleDateString('en-GB') : ''),
		},
		{
			field: 'zones',
			headerName: 'Zones',
			flex: 1,
			renderCell: (c) => <ChipList data={c.value as unknown[]} getLabel={(o: { name?: string }) => o?.name} />,
			sortable: false,
		},
		{
			field: 'groups',
			headerName: 'Companies',
			flex: 1,
			renderCell: (c) => <ChipList data={c.value as unknown[]} getLabel={(o: { name?: string }) => o?.name} showAll />,
			sortable: false,
		},
		{
			field: 'users',
			headerName: 'Users',
			flex: 1,
			renderCell: (c) => (
				<ChipList
					data={(c.value as any[])?.map((user: any) => ({ ...user, status: undefined }))}
					getLabel={(o: { firstName?: string; lastName?: string; email?: string }) =>
						o?.lastName ? `${o?.firstName} ${o?.lastName}` : o?.email
					}
					icon={<User size={14} className="text-gray-500" />}
				/>
			),
			sortable: false,
		},
		{
			field: 'admins',
			headerName: 'Contacts',
			flex: 1,
			renderCell: (c) => (
				<ChipList
					data={(c.value as any[])?.map((user: any) => ({ ...user, status: undefined }))}
					getLabel={(o: { firstName?: string; lastName?: string; email?: string }) =>
						o?.lastName ? `${o?.firstName} ${o?.lastName}` : o?.email
					}
					icon={<User size={14} className="text-gray-500" />}
				/>
			),
			sortable: false,
		},
	];

	if (authLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	const defaultEventData = {
		startTime: startOfDay(new Date()),
		finishTime: endOfDay(new Date()),
		adminIds: authenticatedUser?.id ? [authenticatedUser.id] : [],
	};

	return (
		<>
			<div className="px-4 flex justify-center">
				<div className="max-w-[1280px] w-full">
					<div className="flex flex-row flex-wrap gap-2 mt-2">
						<div key="search" className="w-3/5 flex-grow flex gap-2">
							<FilterSearch
								label="Search Event"
								defaultValue={search.search || ''}
								onChange={(searchInput: string) => handleSearchChange(searchInput)}
								sx={{ width: '100%', minWidth: '40%', flexGrow: 1 }}
							/>
							{verifyPermission([PERMISSIONS.personalAllEventsView]) && (
								<FilterSelect
									options={[
										{ label: 'My Events', value: 'my-events' },
										{ label: 'All Events', value: 'all-events' },
									]}
									value={search.ownerFilter || 'my-events'}
									onChange={(ownerFilter) => handleOwnerFilterChange(ownerFilter)}
									sx={{ width: 330, backgroundColor: 'white' }}
								/>
							)}
							<FilterDateRange
								label="Filter Date Range"
								value={search.from || search.until ? { from: search.from, until: search.until } : undefined}
								onChange={handleDateFilterChange}
								sx={{ width: 330 }}
							/>
							<FilterSelect
								options={[
									{ label: 'Active Events', value: 'active-events' },
									{ label: 'Archived Events', value: 'archived-events' },
								]}
								value={search.archivedFilter || 'active-events'}
								onChange={(archivedFilter: string) => handleArchivedFilterChange(archivedFilter)}
								sx={{ width: 330, backgroundColor: 'white' }}
							/>
							<ConditionalWrapper
								Wrapper={({ children }) => (
									<Tooltip text="This action is not available for your role">{children}</Tooltip>
								)}
								condition={!verifyPermission([PERMISSIONS.personalAllEventsCreate])}
							>
								<Button
									disabled={!verifyPermission([PERMISSIONS.personalAllEventsCreate])}
									startIcon={<AddIcon />}
									variant="outlined"
									onClick={() => setIsFormOpened(true)}
									sx={{ pointerEvents: 'auto !important', flexShrink: 0 }}
								>
									Add event
								</Button>
							</ConditionalWrapper>
						</div>
					</div>
					<Paper variant="outlined" sx={{ marginTop: 2 }}>
						<DataGrid
							data={eventsData?.data || []}
							columns={columns}
							rowCount={eventsData?.count || 0}
							paginationMode="server"
							sortingMode="server"
							isLoading={eventsLoading}
							onPaginationModelChange={handlePaginationModelChange}
							onSortModelChange={handleSort}
							sortingOrder={['asc', 'desc']}
							sortModel={[stringToSort((search.sort || 'startTime:desc') as `${string}:${string}`)]}
							pageSize={eventsData?.pageSize || 25}
							page={eventsData?.page || 0}
							onRowClick={handleRowClick}
							pagination
							getRowClassName={(p) => (p.row.markedForReview ? 'error' : '')}
							onReload={revalidateEvents}
						/>
					</Paper>
				</div>
			</div>
			{isFormOpened && (
				<EventForm
					isOpen={true}
					data={defaultEventData}
					onClose={handleCloseForm}
					onSubmit={handleAddEvent}
					types={types}
					tags={tags}
					singleGroupAsOwner={usesSubEvents}
					usesSubEvents={usesSubEvents}
					usesTags={usesTags}
				/>
			)}
		</>
	);
}
